<script setup>
import {ref, computed, onMounted, onBeforeUnmount} from 'vue';
import {useRouter} from 'vue-router';
import {useAuthService} from '~/composables/useAuthService';
import {useSocialLogin} from '~/composables/useSocialLogin';

const props = defineProps({
  redirectUrl: {
    type: String,
    default: '/',
  },
  type: {
    type: String,
    default: 'login', // 'login' | 'register' | 'prove' (verify only — nobody signed in)
  },
  authId: {
    type: String,
    default: null,
  },
  // After a successful sign-in, attach this pending identity (a blocked Apple/Google
  // sign-in) to the account just signed into — Google ↔ Apple linking.
  linkPendingToken: {
    type: String,
    default: null,
  },
  // Do not offer this provider (it is the one that was just blocked).
  hideProvider: {
    type: String,
    default: null,
  },
});
// Sign-in never creates an account; sign-up may (ACCOUNT_IDENTITY_PLAN.md layer 1).
const emit = defineEmits(['account-not-found', 'identity-proven']);
const intent = computed(() => props.type === 'register' ? 'signUp' : props.type === 'prove' ? 'prove' : 'signIn');
// A provider that could not be set up is NOT offered. Leaving its button on
// screen means a tap that does nothing, which is worse than one fewer way in,
// and leaving it spinning is the defect this came from. The line says which one
// so nobody hunts for a button that used to be there.
const unusable = (name) => (providerProblems.value ?? []).includes(name);
const providerNotice = computed(() => {
  const names = ['google', 'apple'].filter(unusable)
      .map(n => n === 'apple' ? 'Apple' : 'Google');
  if (!names.length) return null;
  return `${names.join(' and ')} sign-in is unavailable right now. Please use another way in.`;
});
const label = (name) => props.type === 'register' ? `Sign up with ${name}` : props.type === 'prove' ? `Continue with ${name}` : `Sign in with ${name}`;

const router = useRouter();
const {
  externalLogin,
  externalLoginItem,
  externalLoginErrors,
  externalLoginLoading,
  hasExternalLoginErrors,
  externalLoginLink,
  externalLoginLinkErrors,
} = useAuthService();

const {
  loginWithGoogle,
  loginWithFacebook,
  loginWithApple,
  googleLoading,
  facebookLoading,
  appleLoading,
  isAppleReady,
  isFacebookReady,
  providerProblems,
  init,
  destroy
} = useSocialLogin({
  onGoogleAuth: async ({ idToken }) => {
    const credentials = { provider: 'google', token: idToken, intent: intent.value };
    if (props.authId) {
      credentials.authId = props.authId;
    }
    await externalLogin(credentials);
    if(!hasExternalLoginErrors.value) {
      await onPostExternalLogin();
    }
  },
  onAppleAuth: async ({ idToken, firstName, lastName }) => {
    const credentials = { provider: 'apple', token: idToken, firstName, lastName, intent: intent.value };
    if (props.authId) {
      credentials.authId = props.authId;
    }
    await externalLogin(credentials);
    if(!hasExternalLoginErrors.value) {
      await onPostExternalLogin();
    }
  },
  onFacebookAuth: async ({ accessToken }) => {
    const credentials = { provider: 'facebook', token: accessToken, intent: intent.value };
    if (props.authId) {
      credentials.authId = props.authId;
    }
    await externalLogin(credentials);
    if(!hasExternalLoginErrors.value) {
      await onPostExternalLogin();
    }
  }
});

onMounted(init);
onBeforeUnmount(destroy);



const onPostExternalLogin = async () => {
  if (hasExternalLoginErrors.value) return;
  // A sign-in nobody uses: nothing was created, no token issued — hand the
  // question (link or create) to the host.
  const res = externalLoginItem.value;
  // Verified only (combine flows): hand the proven identity to the host.
  if (res?.identityProven) {
    emit('identity-proven', {token: res.pendingIdentityToken, provider: res.pendingProvider});
    return;
  }
  if (res?.accountNotFound) {
    emit('account-not-found', {
      token: res.pendingIdentityToken,
      provider: res.pendingProvider,
      email: res.pendingEmail,
      isRelay: !!res.pendingEmailIsPrivateRelay,
    });
    return;
  }
  // They chose "create" but this Apple ID / Google account already had an
  // account — nothing was created, they are signed into the one they had.
  if (res?.existingAccount) {
    const {setAlertMessage} = useAlertService();
    const who = res.loginMethodApple && props.type === 'register' ? 'this Apple ID' : 'this account';
    setAlertMessage({message: `You already had a Tap Leagues account with ${who}, so we signed you in.`, color: 'success'});
  }
  // Signed in with one provider to link the other: both sides are now proven.
  if (props.linkPendingToken && res?.accessToken) {
    await externalLoginLink({pendingIdentityToken: props.linkPendingToken});
    const {setAlertMessage} = useAlertService();
    if (externalLoginLinkErrors.value && Object.keys(externalLoginLinkErrors.value).length) {
      setAlertMessage({message: externalLoginLinkErrors.value.global?.[0] || 'Signed in, but the other sign-in could not be linked', color: 'warning'});
    } else {
      setAlertMessage({message: 'Linked. Next time, one tap signs you in', color: 'success'});
    }
  }
  // A social sign-in that CREATED the account gives the terms and a date of
  // birth first; the setup guard sends it straight on when neither is owed.
  if (externalLoginItem.value?.newAccount === true) {
    await router.push('/details-required?redirectUrl=' + encodeURIComponent(props.redirectUrl || '/'));
    return;
  }
  if (props.redirectUrl) {
    await router.push(props.redirectUrl);
  }
};

</script>

<template>
  <div class="d-flex flex-column align-stretch">
    <v-btn
        v-if="hideProvider !== 'google' && !unusable('google')"
        size="large"
        variant="outlined"
        class="auth-social-btn mb-2"
        :block="true"
        data-testid="social-google"
        @click="loginWithGoogle"
        :loading="googleLoading">
      <v-icon size="18" icon="mdi-google" color="google" class="mr-2"></v-icon>
      {{ label('Google') }}
    </v-btn>

    <v-btn
        v-if="hideProvider !== 'apple' && !unusable('apple')"
        size="large"
        variant="outlined"
        class="auth-social-btn"
        :block="true"
        data-testid="social-apple"
        @click="loginWithApple"
        :loading="appleLoading || !isAppleReady">
      <v-icon size="18" icon="mdi-apple" class="mr-2"></v-icon>
      {{ label('Apple') }}
    </v-btn>

    <!-- Facebook sign-in is discontinued going forward (IMPLEMENTATION-LOG blocker #2):
         the button is no longer rendered, but the OAuth wiring (useSocialLogin /
         loginWithFacebook and the callback routes) stays intact so existing linked
         accounts keep working elsewhere. -->

    <div v-if="providerNotice" class="social-unavailable mt-2">{{ providerNotice }}</div>

    <!-- show-all, or a field-scoped 400 renders nothing and the button looks inert -->
    <crud-error-display class="mt-3" :show-all="true" :errors="externalLoginErrors" v-if="externalLoginErrors"/>
  </div>
</template>

<style scoped>
.social-unavailable { font-size: 12.5px; color: rgba(var(--v-theme-on-surface), 0.6); text-align: center; }
</style>
